import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaTitleTransferGuidePage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">Title Transfer Guide</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada Vehicle Title Transfer Guide</h1>
        <p className="text-obsidian-300">Complete guide to transferring a vehicle title in Nevada — private party sales, gifted vehicles, and more.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Title Transfer Overview</h2>
          <p>A title transfer is required whenever ownership of a vehicle changes hands in Nevada. This includes private party sales, gifts, inheritance, and dealer purchases. The title transfer fee in Nevada is <strong>$28.25</strong> for a complete change of ownership.</p>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Private Party Sale — Seller Responsibilities</h2>
          <ul className="space-y-3">
            {[
              'Sign the back of the title in the designated seller signature area',
              'If title lists owners with "AND" — both must sign; if "OR" — only one signature needed',
              'Fill in the odometer reading (required for vehicles under 10 years old)',
              'Provide a Bill of Sale (Form VP 104) with purchase price, date, and both parties\' information',
              'Remove your license plates — in Nevada, plates stay with the seller, not the vehicle',
              'Notify the DMV of the sale within 10 days to avoid liability for future violations',
            ].map(item => (
              <li key={item} className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-checkbox-circle-line text-green-500"></i></div>
                <span className="text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Buyer Responsibilities</h2>
          <ul className="space-y-3">
            {[
              'Obtain Nevada vehicle insurance before registering',
              'Complete a VIN inspection if the vehicle has never been registered in Nevada',
              'Pass emissions test if required (Clark County vehicles)',
              'Submit the signed title, Bill of Sale, and all required documents to the DMV',
              'Pay title transfer fee ($28.25), registration fees, and governmental services tax',
              'Must complete transfer within 30 days of purchase',
            ].map(item => (
              <li key={item} className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-checkbox-circle-line text-green-500"></i></div>
                <span className="text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Title Transfer Fees</h2>
          <div className="bg-obsidian-50 rounded-xl p-6">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-obsidian-200"><th className="text-left py-2 font-semibold text-obsidian-900">Fee</th><th className="text-right py-2 font-semibold text-obsidian-900">Amount</th></tr></thead>
              <tbody className="divide-y divide-obsidian-100">
                {[['Title Transfer Fee', '$28.25'], ['Registration Fee', '$33.00 (passenger cars)'], ['Governmental Services Tax', 'Based on MSRP & vehicle age'], ['VIN Inspection (if needed)', '$1.00'], ['Emissions Test (if required)', 'Varies by station']].map(([l, a]) => (
                  <tr key={l}><td className="py-2.5 text-obsidian-700">{l}</td><td className="py-2.5 text-right font-medium text-obsidian-900">{a}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Let Us Handle Your Title Transfer</h3>
          <p className="text-obsidian-600 text-sm mb-4">Speedy DMV Services processes title transfers quickly and correctly. We handle all the paperwork so you don&apos;t have to.</p>
          <Link to="/appointment" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaTitleTransferGuidePage;
